import React from 'react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box/Box';
import Stack from '@mui/material/Stack/Stack';
import Card from '@mui/material/Card/Card';
import CardActionArea from '@mui/material/CardActionArea/CardActionArea';
import CardContent from '@mui/material/CardContent/CardContent';
import Typography from '@mui/material/Typography/Typography';
import Chip from '@mui/material/Chip/Chip';
import { keyframes } from '@mui/material/styles';
import { HackathonPayload } from '../../services/hackathon.service';
import { useLiveBoard } from '../../hooks/useLiveSessions';
import { useSessions } from '../../hooks/useSessions';
import { useAuth } from '../../services/useAuth';
import { hasAnyRole, sessionRoles } from '../../utils/sessions';
import { useElapsed } from './useElapsed';

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.6); }
  70% { box-shadow: 0 0 0 8px rgba(239, 68, 68, 0); }
  100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); }
`;

export const LiveDot = () => (
    <Box component="span" aria-hidden="true" sx={{
        width: 10,
        height: 10,
        borderRadius: '50%',
        backgroundColor: '#ef4444',
        display: 'inline-block',
        flexShrink: 0,
        animation: `${pulse} 2s infinite`,
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' }
    }} />
)

const Elapsed = ({ since }: { since: Date | null }) => {
    const elapsed = useElapsed(since);
    return elapsed ? <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>{elapsed}</Typography> : null;
}

/**
 * Broadcast of what each room is presenting right now. Hidden when nothing is live.
 */
export const LiveNowStrip = ({ hackathonId, hackathon }: { hackathonId: string, hackathon: HackathonPayload }) => {
    const { sessions: live, votingPeriodOn } = useLiveBoard(hackathonId);
    const { sessions } = useSessions(hackathonId);
    const { user } = useAuth();
    const email = user ? user.email : undefined;

    // once results are announced nothing is on stage any more, even if a room was never finished
    const active = hackathon.winners?.length ? [] : live.filter(s => s.activeIdea);
    // with one room the live banner already says what's on
    if (active.length === 0 || sessions.length <= 1) {
        return null;
    }
    const isMine = (sessionId: string) => {
        const session = sessions.find(s => s._id === sessionId);
        return !!session && hasAnyRole(sessionRoles(session, hackathon, email));
    };
    const ordered = [...active.filter(s => isMine(s._id)), ...active.filter(s => !isMine(s._id))];

    return (
        <Stack spacing={1.5} component="section" aria-label="Live now">
            <Stack direction="row" spacing={1} alignItems="center">
                <LiveDot />
                <Typography variant="overline" sx={{ fontWeight: 700 }}>Live now</Typography>
            </Stack>
            <Box sx={{
                display: 'grid',
                gap: 1.5,
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' }
            }}>
                {
                    ordered.map(room => {
                        const team = hackathon.ideas.find(i => i._id === room.activeIdea);
                        const mine = isMine(room._id);
                        const remaining = room.ideas.filter(id => id !== room.activeIdea && !room.presented.includes(id)).length;
                        return (
                            <Card key={room._id} sx={mine ? { borderColor: 'primary.main', borderWidth: 2 } : undefined}>
                                <CardActionArea component={Link} to={`/h/${hackathon.slug}/s/${room.slug}`} sx={{ color: 'text.primary', textDecoration: 'none', height: '100%' }}>
                                    <CardContent>
                                        <Stack spacing={0.75}>
                                            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }} noWrap>{room.name}</Typography>
                                                <Elapsed since={room.activeSince} />
                                            </Stack>
                                            <Typography variant="h5" component="p">{team?.title || 'A team'}</Typography>
                                            <Typography variant="body2" color="text.secondary" noWrap>
                                                {team?.members.map(m => m.username).join(', ') || ''}
                                            </Typography>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                {room.votingOpen && votingPeriodOn && <Chip size="small" color="secondary" label="Voting open" />}
                                                <Typography variant="caption" color="text.secondary">
                                                    {remaining === 0 ? 'Last team' : `${remaining} more to go`}
                                                </Typography>
                                            </Stack>
                                        </Stack>
                                    </CardContent>
                                </CardActionArea>
                            </Card>
                        )
                    })
                }
            </Box>
        </Stack>
    )
}
